import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getProfile, getCurrentUser } from '@/lib/api';
import { ProductId, PRODUCT_STORAGE_KEY } from '@/contexts/ProductContext';

/**
 * Hook to get the user's product-aware dashboard path.
 * Priority: location.state > localStorage > profile.primary_product_id > default
 */
export const useProductDashboard = () => {
  const location = useLocation();
  const [dashboardPath, setDashboardPath] = useState<string>(`/${ProductId.MINDCOTINE}/dashboard`);
  const [productId, setProductId] = useState<ProductId>(ProductId.MINDCOTINE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const determineProduct = async () => {
      try {
        // Priority 1: Check location.state (passed from Navbar when navigating to admin)
        const stateProduct = (location.state as { fromProduct?: ProductId })?.fromProduct;
        if (stateProduct && Object.values(ProductId).includes(stateProduct)) {
          setProductId(stateProduct);
          setDashboardPath(`/${stateProduct}/dashboard`);
          setLoading(false);
          return;
        }

        // Priority 2: Check localStorage for last active product
        try {
          const storedProduct = localStorage.getItem(PRODUCT_STORAGE_KEY);
          if (storedProduct && Object.values(ProductId).includes(storedProduct as ProductId)) {
            const product = storedProduct as ProductId;
            setProductId(product);
            setDashboardPath(`/${product}/dashboard`);
            setLoading(false);
            return;
          }
        } catch {
          // localStorage not available
        }

        // Priority 3: Fall back to profile's primary_product_id
        const userResult = await getCurrentUser();
        if (userResult.success && userResult.data) {
          const profileResult = await getProfile(userResult.data.id);
          if (profileResult.success && profileResult.data?.primary_product_id) {
            const product = profileResult.data.primary_product_id as ProductId;
            setProductId(product);
            setDashboardPath(`/${product}/dashboard`);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error determining product dashboard path:', error);
      } finally {
        setLoading(false);
      }
    };

    determineProduct();
  }, [location.state]);

  return { 
    dashboardPath, 
    productId,
    profilePath: `/${productId}/profile`,
    programPath: `/${productId}/program`,
    trackingPath: `/${productId}/tracking`,
    chatPath: `/${productId}/chat`,
    loading 
  };
};
